'use client';

/**
 * AddCreditCardCheckoutFrames Component
 *
 * Handles adding a new credit card payment source using Checkout.com Frames gateway.
 * Uses useCheckout hook for gateway management.
 * Replicates the visual layout of the legacy AddCreditCard/AddCreditCardForm components.
 */

import React, { useState, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import { Popover } from '@barges/react';
import { useCheckout } from '../hooks/useCheckout';
import { createGatewaySettings, getFramesPlaceholders } from './checkoutFramesConfig';
import { updatePaymentSource, ApiError } from '../../api';
import { PURCHASE_FLOW_TEXT_KEYS, PURCHASE_FLOW_ICONS } from '../../utils/constants';
import type { AddPaymentSourceProps } from '../../types';
import { usePurchaseLog, usePurchaseTheme, usePurchaseRender } from '../../config/PurchaseFlowConfigProvider';

import classes from './classes.module.scss';
import componentClasses from '../../components/classes.module.scss';
import { usePurchaseAnalytics } from '../../analytics';

// ============================================================================
// Constants
// ============================================================================

const ZIP_REGEX = /^(?!\s*$).+[a-zA-Z0-9 -]+$/;

// ============================================================================
// Helpers
// ============================================================================

const getInputClassName = (
  { error, loading }: { error: string | null; loading: boolean }
): string => {
  if (loading) return classnames(classes.input, classes.skeleton);
  if (error) return classes.inputError;
  return classes.input;
};

const getFocusStyle = (
  { fieldFocus, tagType, error }: { fieldFocus: string | null; tagType: string; error: string | null }
): React.CSSProperties => {
  // Mirror the SCSS fallback chain in .inputError / .input:focus so Frames
  // mode keeps its red-error and focus borders after the --blue-snap-* var
  // indirection was removed from classes.module.scss.
  if (error) return { borderColor: 'var(--app-blue-snap-input-error-color, #e74c3c)' };
  if (fieldFocus === tagType) return { borderColor: 'var(--app-blue-snap-input-border-focus-color, var(--app-color))', borderWidth: '1px' };
  return {};
};

// ============================================================================
// Component
// ============================================================================

export const AddCreditCardCheckoutFrames: React.FC<AddPaymentSourceProps> = ({
  gatewayConfig,
  paymentSourceId,
  onSuccess,
  onError,
  onRefreshConfig,
  isPayg
}) => {
  const { renderIcon, renderText } = usePurchaseRender();
  // Form state
  const [name, setName] = useState('');
  const [zip, setZip] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [savingCard, setSavingCard] = useState(false);
  const [visibleTooltip, setVisibleTooltip] = useState<string | null>(null);

  const analytics = usePurchaseAnalytics();

  const log = usePurchaseLog();
  const { textColor } = usePurchaseTheme();

  // Error popup state
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);

  // Gateway settings (memoized)
  const gatewaySettings = useMemo(() => createGatewaySettings(renderText), [renderText]);

  const errorTexts = useMemo(() => ({
    cName: renderText(PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_NAME),
    cZip: renderText(PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_ZIP)
  }), [renderText]);

  // Frames placeholders for hook config
  const framesPlaceholders = useMemo(
    () => getFramesPlaceholders(gatewaySettings),
    [gatewaySettings]
  );

  // Memoize validation errors to avoid re-creating the object on every render
  // (it's a dependency of the main useEffect in useCheckout)
  const validationErrors = useMemo(() => ({
    cardNumber: renderText(PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_CARD_NUMBER) as string,
    cvv: renderText(PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_CVV) as string,
    expiryDate: renderText(PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_EXPIRY) as string
  }), [renderText]);

  // Checkout.com Frames/Flow hook
  const {
    isScriptReady,
    isSetupComplete,
    scriptError,
    gatewayErrors,
    fieldFocus,
    cardType,
    isFlowMode,
    submitCard,
    isCardValid,
    validateAllFields,
    reset: resetFrames
  } = useCheckout({
    config: {
      publicKey: gatewayConfig.publicKey || '',
      jsSrc: gatewayConfig.jsSrc,
      paymentSession: gatewayConfig.paymentSession,
      environment: gatewayConfig.environment,
      placeholders: framesPlaceholders,
      textColor,
      validationErrors,
      flowElementId: classes['flow-card-container']
    }
  });

  // ============================================================================
  // Derived state
  // ============================================================================

  const isLoading = !isScriptReady || !isSetupComplete || savingCard;

  // Flow has no per-frame validation events — gate the button on isCardValid()
  // (fed by the card component's onChange). Frames keeps its legacy UX where
  // the button is clickable and handleSubmit calls validateAllFields() to
  // surface per-field errors; don't tighten that here.
  const canSaveCard = !!name.trim()
    && ZIP_REGEX.test(zip)
    && !gatewayErrors.ccn
    && !gatewayErrors.exp
    && !gatewayErrors.cvv
    && !isLoading
    && (!isFlowMode || isCardValid());

  // ============================================================================
  // Handlers
  // ============================================================================

  const clearError = useCallback((key: string) => {
    setErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    clearError('cName');
  }, [clearError]);

  const handleZipChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setZip(e.target.value);
    clearError('cZip');
  }, [clearError]);

  const handleErrorPopupOk = useCallback(() => {
    log('info', 'CC Checkout Frames', 'User dismissed error popup, resetting frames');
    setShowErrorPopup(false);
    setApiErrorMessage(null);
    setErrors({});
    setSavingCard(false);
    // Re-fetch gateway config to get fresh tokens, then re-init Frames
    onRefreshConfig?.();
    resetFrames();
  }, [log, resetFrames, onRefreshConfig]);

  const handleSubmit = useCallback(async () => {
    if (savingCard || !isSetupComplete) return;

    // Validate name and zip
    const formErrors: Record<string, string> = {};
    if (!name.trim()) formErrors.cName = errorTexts.cName;
    if (!ZIP_REGEX.test(zip)) formErrors.cZip = errorTexts.cZip;

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    // Check gateway errors
    if (Object.keys(gatewayErrors).length > 0) return;

    // Check if card is valid — also populates gatewayErrors for untouched fields
    if (!isCardValid()) {
      validateAllFields();
      return;
    }

    setSavingCard(true);
    setErrors({});

    let cardData: any;

    try {
      cardData = await submitCard(name, zip);
    } catch (errorObj: unknown) {
      const errorMessage = (errorObj instanceof ApiError ? errorObj.userMessage : (errorObj as Error)?.message) || '';
      analytics.trackAddCardFailed('api error', { 'error message': errorMessage });
      setApiErrorMessage(errorMessage);
      setShowErrorPopup(true);
      onError(errorMessage);
      setSavingCard(false);
      return;
    }

    const { ccType, scheme } = cardData;

    try {
      log('info', 'CC Checkout Frames', 'Card tokenized, updating payment source');

      const response = await updatePaymentSource(paymentSourceId, cardData, name, zip);

      analytics.setConversionEventId(response.conversion_event_id);
      analytics.trackCardAdded(paymentSourceId, 'cc', ccType || scheme);

      log('info', 'CC Checkout Frames', 'Payment source updated successfully');

      onSuccess({
        id: paymentSourceId,
        paymentMethodId: paymentSourceId,
        paymentMethodKind: 'cc',
        gatewayName: gatewayConfig.gateway_name,
        gatewayConfig,
        isDefault: false
      });
    } catch (errorObj: unknown) {
      log('error', 'CC Checkout Frames', 'Error saving card', errorObj);

      const err = errorObj as Record<string, unknown>;
      analytics.trackAddCardFailed('api error', {
        'error message': err?.message as string,
        'failed reason error code': err?.error_core as string,
        'failed reason description': err?.error_name as string,
        'cc type': scheme
      });

      const errorMessage = (errorObj instanceof ApiError ? errorObj.userMessage : (errorObj as Error)?.message) || '';
      setApiErrorMessage(errorMessage);
      setShowErrorPopup(true);
      onError(errorMessage);
      setSavingCard(false);
    }
  }, [
    log,
    savingCard,
    isSetupComplete,
    name,
    zip,
    errorTexts,
    gatewayErrors,
    isCardValid,
    validateAllFields,
    submitCard,
    paymentSourceId,
    gatewayConfig,
    onSuccess,
    onError
  ]);

  // ============================================================================
  // Error State
  // ============================================================================

  if (scriptError) {
    return (
      <div>
        <div>
          Error loading payment gateway:
          {' '}
          {scriptError.message}
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render helpers
  // ============================================================================

  const renderLabel = (inputType: string) => {
    const text = gatewaySettings.inputLabels?.[inputType];
    if (!text) return null;
    return <div className={ classes.label }>{ text }</div>;
  };

  const renderFieldError = (value: string | null | undefined, testId?: string) => {
    if (!value) return null;
    return <div className={ classes.fieldError } data-testid={ testId }>{ value }</div>;
  };

  const renderCreditCardImg = () => {
    if (!gatewaySettings.needShowCreditCardImg) return null;
    return (
      <span className={ classes.creditCardImg }>
        { renderIcon(cardType || PURCHASE_FLOW_ICONS.UNKNOWN_CARD) }
      </span>
    );
  };

  const renderTooltip = (tooltipType: string) => {
    const tooltipData = gatewaySettings.tooltips?.[tooltipType];
    if (!tooltipData) return null;

    const handleInfoClick = () => {
      setVisibleTooltip(prev => (prev === tooltipType ? null : tooltipType));
    };

    return (
      <>
        { visibleTooltip === tooltipType && (
          <div className={ classes.tooltipContainer } data-testid={ `cc-tooltip-${ tooltipType }` }>
            <div className={ classes.tooltipArrow } />
            { tooltipData.text }
          </div>
        ) }
        <button
          type="button"
          className={ classes.infoButton }
          onClick={ handleInfoClick }
          tabIndex={ -1 }
          data-testid={ `cc-tooltip-button-${ tooltipType }` }
        >
          <span className={ classes.infoImg }>
            { renderIcon(PURCHASE_FLOW_ICONS.CC_INFO) }
          </span>
        </button>
      </>
    );
  };

  const getPlaceholder = (type: string): string => gatewaySettings.placeHolders?.[type] || ' ';

  const getGatewayError = (tagId: string): string | null => gatewayErrors[tagId] || null;

  // PAYG disclaimer: use renderText (purchase flow abstraction); translation may contain <br/> — normalize to newlines
  const paygDisclaimerText = isPayg
    ? String(renderText(PURCHASE_FLOW_TEXT_KEYS.CC_PAYG_DISCLAIMER) ?? '').replace(/<br\s*\/?>/gi, '\n')
    : '';

  // ============================================================================
  // Render
  // ============================================================================

  const errorContent = (
    <div className={ classes.errorContainer } data-testid="cc-error-popup-container">
      <div className={ classes.errorTitle } data-testid="cc-error-popup-title">{ renderText(PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_POPUP_TITLE) }</div>
      <div className={ classes.errorText } data-testid="cc-error-popup-message">{ apiErrorMessage || renderText(PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_POPUP_MESSAGE) }</div>
      <button type="button" className={ classes.errorOkBtn } onClick={ handleErrorPopupOk } data-testid="cc-error-popup-ok-button">
        { renderText(PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_POPUP_OK) }
      </button>
    </div>
  );

  return (
    <>
      {/* PAYG disclaimer (old wallet); renderText via config, <br/> in translation shown as line break */}
      {isPayg && paygDisclaimerText && (
        <div className={ classes.zenModeLabelContainer }>
          <div className={ classes.zenModeLabelText }>{ paygDisclaimerText }</div>
        </div>
      )}
      {/* Form fields */}
      <div
        className={ classnames(classes.formContainer, isFlowMode && classes.flowMode) }
        data-testid="cc-form-container"
      >
        {/* Cardholder Name */}
        <div className={ classes.inputHolder }>
          { renderLabel('cName') }
          <input
            id="nameOfCard"
            type="text"
            value={ name }
            className={ getInputClassName({ error: errors.cName || null, loading: isLoading }) }
            onChange={ handleNameChange }
            placeholder={ getPlaceholder('cName') }
            maxLength={ 30 }
            data-testid="cc-input-name"
          />
          { renderFieldError(errors.cName, 'cc-field-error-name') }
        </div>

        {/* Card fields — Flow uses single container, Frames uses three iframes */}
        {isFlowMode ? (
          <div className={ classes.inputHolder }>
            <div
              id={ classes['flow-card-container'] }
              className={ classnames(classes.flowCardContainer, isLoading && classes.skeleton) }
              data-testid="cc-input-flow-card"
            />
          </div>
        ) : (
          <>
            <div className={ classes.inputHolder }>
              { renderLabel('ccn') }
              <div
                className={ getInputClassName({ error: getGatewayError('ccn'), loading: isLoading }) }
                style={ getFocusStyle({ fieldFocus, tagType: 'ccn', error: getGatewayError('ccn') }) }
                data-testid="cc-input-card-number"
              >
                { renderCreditCardImg() }
                <div className={ classnames('card-number-frame', classes.frameDiv) } />
              </div>
              { renderFieldError(getGatewayError('ccn'), 'cc-field-error-card-number') }
            </div>

            <div className={ classes.expirySecurityRow }>
              <div className={ classes.expirySecurityField }>
                { renderLabel('exp') }
                <div
                  className={ getInputClassName({ error: getGatewayError('exp'), loading: isLoading }) }
                  style={ getFocusStyle({ fieldFocus, tagType: 'exp', error: getGatewayError('exp') }) }
                  data-testid="cc-input-expiry"
                >
                  <div className={ classnames('expiry-date-frame', classes.frameDiv) } />
                  { renderTooltip('exp') }
                </div>
                { renderFieldError(getGatewayError('exp'), 'cc-field-error-expiry') }
              </div>
              <div className={ classes.expirySecuritySpacer } />
              <div className={ classes.expirySecurityField }>
                { renderLabel('cvv') }
                <div
                  className={ getInputClassName({ error: getGatewayError('cvv'), loading: isLoading }) }
                  style={ getFocusStyle({ fieldFocus, tagType: 'cvv', error: getGatewayError('cvv') }) }
                  data-testid="cc-input-cvv"
                >
                  <div className={ classnames('cvv-frame', classes.frameDiv) } />
                  { renderTooltip('cvv') }
                </div>
                { renderFieldError(getGatewayError('cvv'), 'cc-field-error-cvv') }
              </div>
            </div>
          </>
        )}

        {/* Zip/Postal Code */}
        <div className={ classes.inputHolder }>
          { renderLabel('cZip') }
          <input
            id="zip"
            type="text"
            value={ zip }
            className={ getInputClassName({ error: errors.cZip || null, loading: isLoading }) }
            onChange={ handleZipChange }
            placeholder={ getPlaceholder('cZip') }
            maxLength={ 30 }
            data-testid="cc-input-zip"
          />
          { renderFieldError(errors.cZip, 'cc-field-error-zip') }
        </div>
      </div>

      {/* Guaranteed safe + Save button */}
      <div className={ classes.guaranteedSafeAndSaveContainer }>
        <div className={ classes.guaranteedSafeRow }>
          <div className={ classes.guaranteedSafeLockContainer }>
            <div className={ classes.guaranteedSafeLockImgContainer }>
              <span className={ classes.guaranteedSafeLockImg }>
                { renderIcon(PURCHASE_FLOW_ICONS.LOCK) }
              </span>
            </div>
            <div className={ classes.guaranteedSafeText }>
              { renderText(PURCHASE_FLOW_TEXT_KEYS.CC_GUARANTEED_SAFE) }
            </div>
          </div>
          <div className={ classes.guaranteedSafeImgsContainer }>
            <span className={ classes.guaranteedSafeImgs }>
              { renderIcon(PURCHASE_FLOW_ICONS.GUARANTEED_SAFE) }
            </span>
          </div>
        </div>
        <div className={ classes.saveBtnContainer }>
          <button
            className={ canSaveCard ? classes.saveButton : classes.disabledSaveButton }
            type="button"
            onClick={ handleSubmit }
            disabled={ !canSaveCard }
            data-testid="cc-save-button"
          >
            { renderText(PURCHASE_FLOW_TEXT_KEYS.CC_SAVE_CARD) }
          </button>
        </div>
      </div>

      {/* Error: modal for all (web and mobile) */}
      <Popover isOpen={ showErrorPopup } className={ componentClasses.errorPopup } data-testid="cc-error-popup">
        { errorContent }
      </Popover>
    </>
  );
};
